/**
 * Hook para buscar detalhes completos de uma inbox
 * Inclui informações da inbox, credenciais WABA e funções de validação/teste
 * 
 * @version 1.1.0 - Migrado para RPC get_inbox_details_safe (segurança)
 * @date 2026-03-29
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';
import { useAuth } from '@/hooks/useAuth';

// ============================================================================
// INTERFACES
// ============================================================================

/**
 * Interface para dados completos da inbox
 * v1.1.0: Removido tenant_id, provider.id e provider.slug (segurança)
 * Adicionado provider_category para decisões internas
 */
export interface InboxDetailsData {
  id: string;
  name: string;
  identifier: string;
  channel_type: string;
  status: string;
  is_default: boolean;
  created_at: string;
  updated_at: string;
  tenant_id: string;
  provider?: {
    id: string;
    name: string;
    display_name: string | null;
    slug: string;
    logo_url: string | null;
  };
  /** Categoria do provider para decisões internas (meta-whatsapp, meta-instagram, unofficial) */
  provider_category?: string;
}

/**
 * Interface para credenciais WABA seguras (sem access_token)
 */
export interface WabaCredentialsSafe {
  waba_id: string | null;
  phone_number_id: string | null;
  has_access_token: boolean;
  token_expires_at: string | null;
  has_webhook_verify_token: boolean;
  display_phone_number: string | null;
  business_name: string | null;
  last_validated_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}

/**
 * Interface para resultado de validação de conexão
 */
export interface ConnectionValidationResult {
  success: boolean;
  status: 'connected' | 'disconnected' | 'error' | 'no_credentials';
  message: string;
  details?: {
    verified_name?: string;
    quality_rating?: string;
    platform_type?: string;
  };
}

/**
 * Interface para resultado de envio de mensagem de teste
 */
export interface TestMessageResult {
  success: boolean;
  message_id?: string;
  error?: string;
}

/**
 * Interface para credenciais seguras do Instagram
 * Inclui page_id obrigatório para validação de ownership do thread pela API Meta
 */
export interface InstagramCredentialsSafe {
  instagram_business_account_id: string | null;
  page_id: string | null; // ID da Página Facebook - obrigatório para envio de mensagens
  has_access_token: boolean;
  has_app_secret: boolean;
  webhook_verify_token: string | null;
  last_validated_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}

/**
 * Interface para dados completos retornados pelo hook
 */
export interface InboxDetails {
  inbox: InboxDetailsData | null;
  credentials: WabaCredentialsSafe | null;
  instagramCredentials: InstagramCredentialsSafe | null;
  isMetaWhatsApp: boolean;
  isMetaInstagram: boolean;
}

// ============================================================================
// HOOK PRINCIPAL
// ============================================================================

/**
 * Hook para buscar e gerenciar detalhes de uma inbox
 * v1.1.0: Usa RPC get_inbox_details_safe em vez de SELECT direto
 * 
 * @param inboxId - ID da inbox
 * @returns Objeto com dados, funções de ação e estados de loading/error
 */
export function useInboxDetails(inboxId: string | undefined) {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();
  const tenantId = currentTenant?.id;

  // ============================================================================
  // QUERY: BUSCAR INBOX VIA RPC SEGURA
  // ============================================================================

  const inboxQuery = useQuery({
    queryKey: ['inbox-details', inboxId, tenantId],
    queryFn: async (): Promise<InboxDetailsData | null> => {
      if (!inboxId || !tenantId) return null;

      logger.info('useInboxDetails', 'Buscando detalhes da inbox via RPC segura', { inboxId });

      const { data, error } = await supabase.rpc('get_inbox_details_safe', {
        p_inbox_id: inboxId,
        p_tenant_hint: tenantId,
      });

      if (error) {
        logger.error('useInboxDetails', 'Erro ao buscar inbox', error);
        throw error;
      }

      // RPC retorna TABLE, pegar primeiro resultado
      const inboxData = Array.isArray(data) ? data[0] : data;

      if (!inboxData) {
        logger.warn('useInboxDetails', 'Inbox não encontrada', { inboxId });
        return null;
      }

      // Mapear dados para interface mantendo compatibilidade
      const mappedData: InboxDetailsData = {
        id: inboxData.id,
        name: inboxData.name,
        identifier: inboxData.identifier || '',
        channel_type: inboxData.channel_type,
        status: inboxData.status,
        is_default: inboxData.is_default || false,
        created_at: inboxData.created_at,
        updated_at: inboxData.updated_at,
        // v1.1.0: tenant_id não é mais retornado pela RPC, usar do contexto
        tenant_id: tenantId,
        provider: inboxData.provider_display_name ? {
          // v1.1.0: id e slug não são mais expostos
          id: '',
          name: inboxData.provider_display_name,
          display_name: inboxData.provider_display_name,
          slug: inboxData.provider_category || 'unofficial', // Usa category como slug para compatibilidade
          logo_url: inboxData.provider_logo_url,
        } : undefined,
        provider_category: inboxData.provider_category,
      };

      logger.info('useInboxDetails', 'Inbox carregada com sucesso', { inboxId });
      return mappedData;
    },
    enabled: !!inboxId && !!tenantId,
  });

  // ============================================================================
  // QUERY: BUSCAR CREDENCIAIS WABA
  // ============================================================================

  const credentialsQuery = useQuery({
    queryKey: ['inbox-credentials', inboxId, tenantId],
    queryFn: async (): Promise<WabaCredentialsSafe | null> => {
      if (!inboxId || !tenantId) return null;

      logger.info('useInboxDetails', 'Buscando credenciais WABA', { inboxId });

      const { data, error } = await supabase
        .rpc('get_inbox_credentials_safe', {
          p_inbox_id: inboxId,
          p_tenant_hint: tenantId,
        });

      if (error) {
        // Erro P0002 = inbox não encontrada ou sem credenciais
        if (error.code === 'P0002') {
          logger.debug('useInboxDetails', 'Sem credenciais para inbox', { inboxId });
          return null;
        }
        logger.error('useInboxDetails', 'Erro ao buscar credenciais', error);
        throw error;
      }

      // A função retorna um array (TABLE), pegamos o primeiro resultado
      const credentials = Array.isArray(data) ? data[0] : data;
      
      if (!credentials) {
        logger.debug('useInboxDetails', 'Nenhuma credencial encontrada', { inboxId });
        return null;
      }

      logger.info('useInboxDetails', 'Credenciais carregadas', { 
        inboxId, 
        hasToken: credentials.has_access_token 
      });

      return credentials as WabaCredentialsSafe;
    },
    // v1.1.0: Usa provider_category em vez de provider.slug
    enabled: !!inboxId && !!tenantId && inboxQuery.data?.provider_category === 'meta-whatsapp',
  });

  // ============================================================================
  // QUERY: BUSCAR CREDENCIAIS INSTAGRAM
  // ============================================================================

  const instagramCredentialsQuery = useQuery({
    queryKey: ['instagram-credentials', inboxId, tenantId],
    queryFn: async (): Promise<InstagramCredentialsSafe | null> => {
      if (!inboxId || !tenantId) return null;

      logger.info('useInboxDetails', 'Buscando credenciais Instagram', { inboxId });

      // Buscar credenciais diretamente da tabela inbox_credentials
      const { data, error } = await supabase
        .from('inbox_credentials')
        .select('credentials, created_at, updated_at, last_validated_at')
        .eq('inbox_id', inboxId)
        .maybeSingle();

      if (error) {
        logger.error('useInboxDetails', 'Erro ao buscar credenciais Instagram', error);
        throw error;
      }
      
      if (!data?.credentials) {
        logger.debug('useInboxDetails', 'Nenhuma credencial Instagram encontrada', { inboxId });
        return null;
      }

      // Mapear credenciais para interface segura (sem expor tokens)
      // Inclui page_id obrigatório para validação de ownership do thread pela API Meta
      const creds = data.credentials as Record<string, unknown>;
      
      const safeCredentials: InstagramCredentialsSafe = {
        instagram_business_account_id: (creds.instagram_business_account_id as string) || null,
        page_id: (creds.page_id as string) || null, // ID da Página Facebook
        has_access_token: !!(creds.access_token),
        has_app_secret: !!(creds.app_secret),
        webhook_verify_token: (creds.webhook_verify_token as string) || null,
        last_validated_at: data.last_validated_at,
        created_at: data.created_at,
        updated_at: data.updated_at,
      };

      logger.info('useInboxDetails', 'Credenciais Instagram carregadas', { 
        inboxId, 
        hasToken: safeCredentials.has_access_token 
      });

      return safeCredentials;
    },
    // v1.1.0: Usa provider_category em vez de provider.slug
    enabled: !!inboxId && !!tenantId && inboxQuery.data?.provider_category === 'meta-instagram',
  });

  // ============================================================================
  // FUNÇÃO: VERIFICAR CONEXÃO
  // ============================================================================

  const checkConnection = async (): Promise<ConnectionValidationResult> => {
    if (!inboxId || !tenantId) {
      return {
        success: false,
        status: 'error',
        message: 'ID da inbox não informado',
      };
    }

    // v1.1.0: Usa provider_category em vez de provider.slug
    const providerCategory = inboxQuery.data?.provider_category;
    if (!providerCategory) {
      return {
        success: false,
        status: 'error',
        message: 'Provider não identificado',
      };
    }

    logger.info('useInboxDetails', 'Verificando conexão', { inboxId, providerCategory });

    try {
      // CORREÇÃO v1.0.2: Usar action 'get_connection_status' para providers Meta (Instagram/WhatsApp)
      const { data, error } = await supabase.functions.invoke('provider-proxy', {
        body: {
          action: 'get_connection_status',
          inbox_id: inboxId,
        },
      });

      if (error) {
        logger.error('useInboxDetails', 'Erro ao verificar conexão', error);
        return {
          success: false,
          status: 'error',
          message: error.message || 'Erro ao verificar conexão',
        };
      }

      // Tratar resposta do provider-proxy para Instagram/WhatsApp
      if (data?.success && data?.data?.connected) {
        // Invalidar cache para refletir novo status
        queryClient.invalidateQueries({ queryKey: ['inbox-details', inboxId] });
        
        return {
          success: true,
          status: 'connected',
          message: data.data.message || 'Conexão ativa com Meta Graph API',
          details: {
            verified_name: data.data.username || data.data.name,
            platform_type: providerCategory === 'meta-instagram' ? 'Instagram Business' : 'WhatsApp Business',
          },
        };
      }

      // Verificar se é status pending_setup (não é erro, é configuração incompleta)
      if (data?.data?.status === 'pending_setup') {
        return {
          success: false,
          status: 'no_credentials',
          message: data.data.message || 'Credenciais não configuradas',
        };
      }

      return {
        success: false,
        status: 'disconnected',
        message: data?.data?.message || data?.error || 'Conexão não estabelecida',
      };
    } catch (err) {
      logger.error('useInboxDetails', 'Exceção ao verificar conexão', err);
      return {
        success: false,
        status: 'error',
        message: 'Erro inesperado ao verificar conexão',
      };
    }
  };

  // ============================================================================
  // FUNÇÃO: ENVIAR MENSAGEM DE TESTE
  // ============================================================================

  const sendTestMessage = async (
    phone: string, 
    content?: string
  ): Promise<TestMessageResult> => {
    if (!inboxId || !tenantId) {
      return {
        success: false,
        error: 'ID da inbox não informado',
      };
    }

    // v1.1.0: Usa provider_category em vez de provider.slug
    const providerCategory = inboxQuery.data?.provider_category;
    if (!providerCategory) {
      return {
        success: false,
        error: 'Provider não identificado',
      };
    }

    logger.info('useInboxDetails', 'Enviando mensagem de teste', { inboxId, phone });

    try {
      // CORREÇÃO v1.0.1: Campos alinhados com Edge Function send-message
      const { data, error } = await supabase.functions.invoke('send-message', {
        body: {
          inbox_id: inboxId,
          phone: phone,
          message_type: 'text',
          content: content || 'Teste de conexão - UaizyChat ✅',
          source_type: 'test',
        },
      });

      if (error) {
        logger.error('useInboxDetails', 'Erro ao enviar mensagem de teste', error);
        return {
          success: false,
          error: error.message || 'Erro ao enviar mensagem',
        };
      }

      if (data?.success) {
        logger.info('useInboxDetails', 'Mensagem de teste enviada', { 
          messageId: data.message_id 
        });
        return {
          success: true,
          message_id: data.message_id,
        };
      }

      return {
        success: false,
        error: data?.error || 'Falha ao enviar mensagem',
      };
    } catch (err: any) {
      logger.error('useInboxDetails', 'Exceção ao enviar mensagem de teste', err);
      return {
        success: false,
        error: err.message || 'Erro inesperado ao enviar mensagem',
      };
    }
  };

  // ============================================================================
  // FUNÇÃO: INVALIDAR CACHE
  // ============================================================================

  const refetch = () => {
    queryClient.invalidateQueries({ queryKey: ['inbox-details', inboxId] });
    queryClient.invalidateQueries({ queryKey: ['inbox-credentials', inboxId] });
  };

  // ============================================================================
  // RETORNO
  // ============================================================================

  // v1.1.0: Usa provider_category em vez de provider.slug para decisões
  const isMetaWhatsApp = inboxQuery.data?.provider_category === 'meta-whatsapp';
  const isMetaInstagram = inboxQuery.data?.provider_category === 'meta-instagram';

  return {
    // Dados
    data: {
      inbox: inboxQuery.data ?? null,
      credentials: credentialsQuery.data ?? null,
      instagramCredentials: instagramCredentialsQuery.data ?? null,
      isMetaWhatsApp,
      isMetaInstagram,
    } as InboxDetails,
    
    // Estados
    isLoading: inboxQuery.isLoading,
    isLoadingCredentials: credentialsQuery.isLoading || instagramCredentialsQuery.isLoading,
    error: inboxQuery.error,
    
    // Ações
    refetch,
    checkConnection,
    sendTestMessage,
  };
}
